import React, {useEffect, useMemo, useState} from 'react';
import {Select, Space, Input, Button, Tag, Typography, Tooltip, message} from 'antd';
import {PlusOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {fetchWrapper} from '../../helpers';
import {useTranslation} from 'react-i18next';

export default function CollaboratorsPicker({
                                                value,
                                                onChange,
                                                disabled = false,
                                                isStories = false,
                                                max = 5,
                                            }) {
    const {t} = useTranslation();
    const [input, setInput] = useState('');
    const [past, setPast] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                const res = await fetchWrapper.get(`${process.env.REACT_APP_API_URL}/post/collaborators`);
                if (mounted) setPast(res?.collaborators || []);
            } catch {
                // noop
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const current = useMemo(() => Array.isArray(value) ? value : [], [value]);
    const left = Math.max(0, max - current.length);

    const normalize = (name) =>
        String(name || '')
            .trim()
            .replace(/^@/, '')
            .toLowerCase()
            .replace(/[^a-z0-9._]/gi, '');

    const add = (raw) => {
        if (disabled || isStories) return;
        const name = normalize(raw ?? input);
        if (!name) return;

        if (current.includes(name)) {
            message.info(t('functions.alreadyExists'));
            return;
        }
        if (current.length >= max) {
            message.warning(t('postPage.message.collabLimit', {max}));
            return;
        }
        onChange([...current, name]);
        setInput('');
    };

    const remove = (name) => {
        if (disabled || isStories) return;
        onChange(current.filter((c) => c !== name));
    };

    const canAdd = !disabled && !isStories && input.trim();

    return (
        <Space direction="vertical" style={{width: '100%'}} size={8}>
            <Space align="center">
                <Typography.Text strong>
                    {t('postPage.cards.collaborators')}
                </Typography.Text>
                <Tooltip
                    placement="top"
                    title={t(
                        'postPage.hint.collaborators',
                    )}
                >
                    <QuestionCircleOutlined style={{color: '#8c8c8c', fontSize: 14, cursor: 'help'}}/>
                </Tooltip>
                <Typography.Text type={left === 0 ? 'danger' : 'secondary'} style={{marginLeft: 8}}>
                    {`${current.length}/${max}`}
                </Typography.Text>
            </Space>

            {isStories && (
                <Typography.Text type="secondary" style={{display: 'block'}}>
                    {t('posts.storiesWithoutCollab')}
                </Typography.Text>
            )}

            {!disabled && !isStories && past.length > 0 && (
                <Select
                    showSearch
                    allowClear
                    placeholder={t('postPage.placeholders.selectPastCollaborator')}
                    style={{width: '100%'}}
                    onChange={(v) => v && add(v)}
                    value={null}
                    loading={loading}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={past.map((c) => ({value: c, label: `@${c}`}))}
                />
            )}

            <Space style={{width: '100%'}}>
                <Input
                    placeholder={t('postPage.placeholders.collaboratorsPlaceholder')}
                    value={input}
                    disabled={disabled || isStories}
                    onChange={(e) => setInput(e.target.value)}
                    onPressEnter={() => canAdd && add()}
                />
                <Button
                    type="primary"
                    icon={<PlusOutlined/>}
                    disabled={!canAdd}
                    onClick={() => add()}
                >
                    {t('postPage.buttons.add')}
                </Button>
            </Space>

            <div style={{marginTop: 8}}>
                {current.length > 0 ? (
                    <Space size={[0, 8]} wrap>
                        {current.map((name) => (
                            <Tag key={name} closable={!disabled && !isStories} onClose={() => remove(name)}
                                 style={{margin: '4px'}}>
                                @{name}
                            </Tag>
                        ))}
                    </Space>
                ) : (
                    <Typography.Text type="secondary" style={{fontSize: 12}}>
                        {t('postPage.placeholders.collaboratorsNoPlaceholder')}
                    </Typography.Text>
                )}
            </div>

            <Typography.Text type="secondary" style={{fontSize: 12}}>
                {t(
                    'postPage.placeholders.collaboratorsAddPlaceholder',
                )}
            </Typography.Text>
        </Space>
    );
}
